const en = {
  brand: 'ArriveSafe',
  meta: {
    titleDefault: 'ArriveSafe',
    titleTravelling: 'ArriveSafe – Follow {name} on the way',
    titleArrived: 'ArriveSafe – {name} arrived safely',
    titleEmergency: 'ArriveSafe – {name} triggered an emergency',
  },
  /**
   * Share-preview copy (og:title / og:description). Shorter and more
   * self-explanatory than `meta.*`, because it's read by someone who may never
   * have heard of ArriveSafe and is deciding whether to tap a link in a chat.
   *
   * No emergency variant, deliberately — see `lib/socialMeta.ts`.
   */
  social: {
    titleDefault: 'Sharing my location',
    descriptionDefault: 'Watch me until I arrive safely.',
    titleTravelling: '{name} is on the way',
    descriptionTravelling: 'Follow along live until {name} arrives safely.',
    titleArrived: '{name} arrived safely',
    descriptionArrived: 'The journey is complete.',
  },
  status: {
    onTheWay: '{name} is on the way',
    onTheWayTo: '{name} is on the way to {destination}',
    arrived: '{name} arrived safely at {time}',
    arrivedNoTime: '{name} arrived safely',
    emergency: '{name} triggered an emergency',
    titleSubtitle: '{name} is on the way',
  },
  transport: {
    foot: 'on foot',
    running: 'running',
    bike: 'by bike',
    car: 'by car',
    motorcycle: 'by motorcycle',
    bus: 'by bus',
    train: 'by train',
    plane: 'by plane',
    boat: 'by boat',
    horse: 'on horseback',
    detecting: 'detecting…',
  },
  connection: {
    connecting: 'Connecting',
    live: 'Live',
    stale: 'Signal weak',
    offline: 'Offline',
  },
  stats: {
    battery: 'Battery',
    speed: 'Speed',
    avgSpeed: 'Avg speed',
    distance: 'Distance',
    time: 'Time',
    startAddress: 'Start',
    finishAddress: 'Arrival',
    lastUpdate: 'Updated {seconds}s ago',
    lastUpdateMin: 'Updated {minutes} min ago',
    justUpdated: 'Just updated',
  },
  welcome: {
    prompt: '{name} invited you as a companion.',
    askName: 'What name would you like to appear as?',
    nameLabel: 'Your name',
    save: 'Save name',
    skip: 'Skip',
    watchingAs: 'Watching as {name}',
    setYourName: 'Set your name',
  },
  download: {
    title: 'Get ArriveSafe',
    blurb: 'Share your own journeys with friends.',
    ios: 'App Store',
    android: 'Google Play',
    chip: 'Get the app',
    chipDismiss: 'Dismiss',
    iosCta: 'Get on the App Store',
    androidCta: 'Get it on Google Play',
    otherCta: 'Get ArriveSafe',
  },
  loading: {
    title: 'Loading journey…',
    verifying: 'Verifying share code…',
    fetching: 'Loading route…',
  },
  empty: {
    notFound: 'This route could not be found.',
    noCode: 'Open this page with a share code: {url}',
    error: 'Something went wrong: {message}',
  },
  playback: {
    // Aligns with the watch URL `way.arrivesafe.app` and the brand phrase
    // "{name} is on the way" — same wording for both initial and post-completion
    // states so the button label doesn't switch midway through a session.
    play: 'Replay way',
    playing: 'Playing…',
    pause: 'Pause',
    resume: 'Resume',
    replay: 'Replay way',
    pausedAt: 'Paused at {time}',
  },
  timeline: {
    title: 'Journey log',
    close: 'Close',
    routeStarted: 'Journey started',
    batteryDrop: 'Battery at {battery}%',
    stationary: 'Stationary for {duration}',
    stillEnRoute: 'Still on the way',
    stillEnRouteProgress: 'Still on the way · {progress}%',
    arrived: 'Arrived',
    emergency: 'Emergency triggered',
    emergencyAlerted: 'Emergency triggered · {count} contacts alerted',
    companionJoined: '{name} joined as a companion',
    companionJoinedUnnamed: 'A companion joined',
  },
  emergency: {
    bannerActive: '{name} triggered an emergency',
    bannerCleared: '{name} marked themselves safe at {time}',
    triggeredAgo: '{relative} ago',
    lastKnown: 'Last known location',
    callServices: 'Call emergency services ({number})',
    copyLocation: 'Copy location',
    openInMaps: 'Open in Maps',
    enableAlerts: 'Tap to enable sound alerts',
    wrongCountry: 'Wrong country? Change',
  },
  ariaLive: {
    travelling: '{name} is travelling.',
    arrived: '{name} has arrived safely.',
    emergency: '{name} has triggered an emergency. Last known location: {location}.',
  },
} as const;

export default en;

/**
 * Loosen each leaf string from a literal-type ("on foot") to the general
 * `string` so other locale files can satisfy the same shape with their own
 * translations. The key structure stays exact — adding/renaming a key in
 * en.ts breaks the type-check on every other locale, which is what we want.
 */
type Loose<T> = T extends string ? string : { [K in keyof T]: Loose<T[K]> };

export type Dictionary = Loose<typeof en>;
